import React from 'react';
import { Link } from 'react-router-dom';
import { Sun, Moon, Eye, Monitor } from 'lucide-react';

/**
 * Single navigation link, used in both desktop bar and mobile drawer.
 * variant='desktop' (compact horizontal pill) or 'mobile' (full-width row).
 */
export function NavLinkItem({
    to,
    icon: Icon,
    label,
    isActive,
    variant = 'desktop',
    onClick,
    testId,
    accent = 'green', // 'green' | 'gold'
}) {
    const activeColors = accent === 'gold'
        ? 'bg-[#FFD700]/10 text-[#FFD700]'
        : 'bg-[#00FF66]/10 text-[#00FF66]';
    const inactiveColors = accent === 'gold'
        ? 'text-[#FFD700]/70 hover:text-[#FFD700] hover:bg-[#FFD700]/10'
        : 'text-white/70 hover:text-white hover:bg-white/5';

    const desktopClass = `flex items-center gap-2 px-3.5 py-2.5 rounded-lg text-[15px] font-medium transition-all duration-200 whitespace-nowrap shrink-0 ${isActive ? activeColors : inactiveColors}`;
    const mobileClass = `flex items-center gap-3 px-4 py-3 rounded-lg ${isActive ? activeColors : (accent === 'gold' ? inactiveColors : 'text-white/60 hover:text-white hover:bg-white/5')}`;

    return (
        <Link
            to={to}
            onClick={onClick}
            data-testid={testId}
            title={label}
            className={variant === 'desktop' ? desktopClass : mobileClass}
        >
            <Icon className={variant === 'desktop' ? 'w-[18px] h-[18px] shrink-0' : 'w-5 h-5'} />
            <span>{label}</span>
        </Link>
    );
}

/**
 * Theme switcher used both in desktop top bar and mobile drawer.
 */
export function ThemeButtons({ theme, setTheme, variant = 'desktop' }) {
    const sizes = variant === 'desktop'
        ? { wrap: 'flex items-center rounded-lg p-0.5 bg-white/5 border border-white/10', btn: 'p-1.5 rounded-md', icon: 'w-3.5 h-3.5' }
        : { wrap: '', btn: 'p-2 rounded-lg', icon: 'w-4 h-4' };

    const buttons = [
        { id: 'light', Icon: Sun, activeColor: 'bg-[#FFD700]/20 text-[#FFD700]', title: 'Light mode' },
        { id: 'dark', Icon: Moon, activeColor: 'bg-[#9B6FE8]/20 text-[#9B6FE8]', title: 'Dark mode' },
        { id: 'high-contrast', Icon: Eye, activeColor: 'bg-[#00FFFF]/20 text-[#00FFFF]', title: 'High Contrast' },
        { id: 'auto', Icon: Monitor, activeColor: 'bg-[#00FF66]/20 text-[#00FF66]', title: 'Auto' },
    ];

    return (
        <div className={sizes.wrap} data-testid={variant === 'desktop' ? 'theme-toggle' : undefined} role={variant === 'desktop' ? 'radiogroup' : undefined} aria-label={variant === 'desktop' ? 'Theme selector' : undefined}>
            {buttons.map(b => {
                const Icon = b.Icon;
                const active = theme === b.id;
                return (
                    <button
                        key={b.id}
                        onClick={() => setTheme(b.id)}
                        className={`${sizes.btn} transition-all ${active ? b.activeColor : 'text-white/40 hover:text-white/60'}`}
                        title={b.title}
                        data-testid={variant === 'desktop' ? `theme-${b.id === 'high-contrast' ? 'hc' : b.id}-btn` : undefined}
                        role={variant === 'desktop' ? 'radio' : undefined}
                        aria-checked={variant === 'desktop' ? active : undefined}
                        aria-label={b.title}
                    >
                        <Icon className={sizes.icon} />
                    </button>
                );
            })}
        </div>
    );
}
